import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from "@angular/core";
import { DxDataGridComponent } from "devextreme-angular";
import { custom } from "devextreme/ui/dialog";
import { Subscription, forkJoin } from "rxjs";
import { Expense } from "src/app/_api/models/expense.model";
import { ExpensesService } from "src/app/_api/services/expenses.service";
import { StatusUpdateEventService } from "src/app/modules/time/timesalaryapproval/status-update-event.service";
import { TimesalaryapprovalDetailTimeComponent } from "src/app/modules/time/timesalaryapproval/timesalaryapproval-detail/timesalaryapproval-detail-time/timesalaryapproval-detail-time.component";
import { AmsNotifierService } from "src/app/shared/services/ams-notifier.service";
import { DateRangeEventService } from "src/app/shared/services/date-range-event.service";
import { LanguageService } from "src/app/shared/services/language.service";
import { UserService } from "src/app/shared/services/user.service";
import { TimeService } from "src/app/modules/time/_api/services/time.service";
import * as moment from 'moment';
import { PortalTypeService } from "../../../../../../shared/services/portal-type.service";

@Component({
  selector: "app-project-portal-approval-employee-expenses",
  templateUrl: "./expenses.component.html",
  styleUrls: ["./expenses.component.css"],
})
export class ProjectPortalApprovalEmployeeExpensesComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() portalType: string;
  @Input() expenses: Expense[];
  @Input() expenseTypes: any[];
  @Input() selectedTimeRecord: any;
  @Input() allowEdit: boolean;
  @Input() timeSalaryTimeComponent: TimesalaryapprovalDetailTimeComponent;
  @Input() selectedProjectId: number;
  @Input() projectReadAll = false;

  @Output() isBusy = new EventEmitter<boolean>();
  @Output() refresh = new EventEmitter<boolean>(); //false if not clearing datagrid selections
  @Output() editingStart = new EventEmitter<any>();
  @Output() loading = new EventEmitter<boolean>(); //emit event to parent(project-portal.component) to show load indicator
  @Output() cellPrepared = new EventEmitter<any>();

  expensesNotConnectedToTime: Expense[] = [];
  filteredExpenses: Expense[] = [];

  expenseText: string;
  isClone: boolean = false;
  cloneData: any;

  @ViewChild("datagriddx", { static: false })
  expensesGrid: DxDataGridComponent;

  dateRangeEventSubscription: Subscription;
  dateInterval: any = { FromDate: moment(), ToDate: moment() };

  approvalFunction: number;

  constructor(
    public languageService: LanguageService,
    private expensesService: ExpensesService,
    private amsNotifierService: AmsNotifierService,
    private dateRangeEventService: DateRangeEventService,
    private statusUpdateEventService: StatusUpdateEventService,
    private userService: UserService,
    private timeService: TimeService,
    private portalTypeService: PortalTypeService) {

    this.dateRangeEventSubscription = this.dateRangeEventService.onDateRangeChanged().subscribe((dateInterval) => {
      this.dateInterval = dateInterval;
      this.loadExpensesNotConnectedToTime();
    });

    this.statusUpdateEventService.onApproveRequested().subscribe(() => {
      this.approve();
    });

    this.statusUpdateEventService.onResetRequested().subscribe((statusId: number) => {
      this.reset(statusId);
    });
  }

  ngAfterViewInit() {
    this.approvalFunction = this.portalTypeService.isProjectPortal( this.portalType) ? 2 : 1;
  }

  ngOnChanges(changes: SimpleChanges) {
    const hasChangedSelectedTimeRecord = changes.selectedTimeRecord !== undefined && changes.selectedTimeRecord.previousValue !== changes.selectedTimeRecord.currentValue;
    const hasChangedExpenses = changes.expenses !== undefined && changes.expenses.previousValue !== changes.expenses.currentValue;
    const hasChangedProjectId = changes.selectedProjectId !== undefined && changes.selectedProjectId.previousValue !== changes.selectedProjectId.currentValue;

    if (hasChangedSelectedTimeRecord === false && hasChangedExpenses === false && hasChangedProjectId === false) { return; }

    if(hasChangedProjectId) this.loadExpensesNotConnectedToTime();

    this.filterExpenses();
  }

  ngOnDestroy() {
    this.dateRangeEventSubscription.unsubscribe();
  }

  getSelectedRows() {
    return this.expensesGrid.instance.getSelectedRowKeys();
  }

  getSelectedRowsData() {
    return this.expensesGrid.instance.getSelectedRowsData();
  }

  selectAll() {
    this.expensesGrid.instance.selectAll();
  }

  deselectAll() {
    this.expensesGrid.instance.deselectAll();
  }

  async loadExpensesNotConnectedToTime() {
    if (!this.dateInterval) return;

    this.expensesNotConnectedToTime = this.selectedProjectId > 0 ?
      await this.expensesService.getByProjectWithinPeriod(this.selectedProjectId, this.dateInterval.FromDate, this.dateInterval.ToDate).toPromise() :
      await this.expensesService.getNotConnectedToTimeWithinPeriod(this.dateInterval.FromDate, this.dateInterval.ToDate).toPromise();
    this.filterExpenses();
  }

  private async loadExpenses(): Promise<any> {
    if (this.selectedTimeRecord.CategoryId === 9 || this.selectedTimeRecord.CategoryId === 8 || this.selectedTimeRecord.CategoryId === 7) {
      this.expenses = await this.expensesService
        .getByTime(this.selectedTimeRecord.TimeId)
        .toPromise();
    }
    else {
      this.expenses = await this.expensesService
        .getByTimeEmployeeWithinPeriod(this.selectedTimeRecord.TimeId, this.selectedTimeRecord.EmployeeId, this.dateInterval.FromDate, this.dateInterval.ToDate)
        .toPromise();
    }

    this.filterExpenses();
  }

  private filterExpenses() {
    if (this.selectedTimeRecord) {
      this.filteredExpenses = this.expenses.filter(a => a.EmployeeId === this.selectedTimeRecord.EmployeeId && a.Date === this.selectedTimeRecord.Date);
      return;
    }

    this.filteredExpenses = this.userService.account.IsAdmin || this.userService.account.IsSupervisor || this.projectReadAll
      ? this.expensesNotConnectedToTime  
      : this.expensesNotConnectedToTime.filter(a => a.EmployeeId === this.userService.account.EmployeeId);
  }

  onToolbarPreparing(e: any) {
    if (this.allowEdit) {
      e.toolbarOptions.items.unshift(
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "copy",
            title: this.languageService.LNG("Label.Copy"),
            onClick: this.onCopySelected.bind(this),
          },
        },
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "trash",
            title: this.languageService.LNG("Label.Delete"),
            onClick: this.onDeleteSelected.bind(this),
          },
        }
      );
    }
  }

  onCopySelected() {
    let selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();
    if (selectedRowsData.length > 0) {
      for (let i = 0; i < selectedRowsData.length; i++) {
        this.isClone = true;
        this.cloneData = selectedRowsData[i];
        this.expensesGrid.instance.addRow();
      }
    }
  }

  onDeleteSelected() {
    let myDialog = custom({
      title: this.languageService.LNG("Label.Delete"),
      messageHtml: this.languageService.LNG(
        "Label.Do.You.Want.To.Delete.The.Selected.Rows"
      ),
      buttons: [
        {
          text: this.languageService.LNG("Label.Yes"),
          onClick: (e) => {
            return true;
          },
        },
        {
          text: this.languageService.LNG("Label.No"),
          onClick: (e) => {
            return false;
          },
        },
      ],
    });
    myDialog.show().then((confirmed: boolean) => {
      if (!confirmed) { return; }

      const expenseIds: number[] = [];
      const selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();

      if (selectedRowsData.length === 0)
      {
        this.amsNotifierService.info(this.languageService.LNG("Label.Please.Select.Rows.To.Delete"));
        return;
      }

      for (let i = 0; i < selectedRowsData.length; i++) {
        expenseIds.push(selectedRowsData[i].ExpenseId);
      }

      if (expenseIds.length === 0) { return; }

      this.onLoading(true);
      this.expensesService.deleteByExpenseIds(expenseIds).subscribe(
        (result: any) => {
          this.onLoading(false);
          this.selectedTimeRecord ? this.loadExpenses() : this.loadExpensesNotConnectedToTime();

          if (result.IsSuccess) {
            this.amsNotifierService.success(this.languageService.LNG("Label.DeletedOK"));

            if (result.Message != null && result.Message.length > 0) {
              this.amsNotifierService.error(result.Message);
            }

            this.refresh.emit(false);
            this.expensesGrid.selectedRowKeys = [];
          } else {
            this.amsNotifierService.error(result.ErrorMessage);
          }
        },
        (error) => {
          this.onLoading(false);
        }
      );
    });
  }

  onEditorPreparing(e: any) {
    if (e.parentType == "dataRow") {
      e.editorOptions.onFocusIn = function (args) {
        const input = args.element.querySelector(".dx-texteditor-input");
        if (input !== null && input !== undefined) {
          input.select();
        }
      };
    }
  }

  onCellPrepared(e: any) {
    if (this.selectedTimeRecord) { return; }
    this.cellPrepared.emit(e);
  }

  onEditingStart(e: any) {
    this.editingStart.emit(e);
  }

  onSavingMultiple(e: any) {
    e.cancel = true;

    const newExpenses = [];
    const updatedExpenses = [];

    this.onLoading(true);

    e.changes.forEach((row: any) => {
      const expense = this.selectedTimeRecord ? this.expenseRecordConnectedToTime(row) : this.expenseRecordNotConnectedToTime(row);

      if (row.type === "insert") {
        newExpenses.push(expense);
      }
      if (row.type === "update") {
        updatedExpenses.push(expense);
      }
    });

    this.expensesService
      .saveMultipleExpenses(newExpenses, updatedExpenses)
      .subscribe((result: any) => {
        if (result.IsSuccess) {
          this.onLoading(false);
          this.selectedTimeRecord ? this.loadExpenses() : this.loadExpensesNotConnectedToTime();

          this.amsNotifierService.success(this.languageService.LNG("Label.SavedOK"));
          if (result.Message != null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }
          e.component.cancelEditData();
        } else {
          this.amsNotifierService.error(result.ErrorMessage);
        }
      },
      (error) => {
      }
    );

    e.component.refresh(true);
    e.component.cancelEditData();
  }

  expenseRecordConnectedToTime(row: any) {
    return {
      ExpenseId: row.type == "insert" ? -1 : row.key.ExpenseId,
      ExpenseTypeId: row.data.ExpenseTypeId ? row.data.ExpenseTypeId : row.key.ExpenseTypeId,
      Value: row.data.Value ? row.data.Value : row.key.Value,
      CarrierId: this.selectedTimeRecord.CarrierId,
      EmployeeId: this.selectedTimeRecord.EmployeeId,
      CategoryId: this.selectedTimeRecord.CategoryId,
      Date: this.selectedTimeRecord.Date,
      Comment: row.data.Comment !== undefined ? row.data.Comment : row.key.Comment
    };
  }

  expenseRecordNotConnectedToTime(row: any) {
    return {
      ExpenseId: row.type == "insert" ? -1 : row.key.ExpenseId,
      StatusId: row.data.StatusId ? row.data.StatusId : row.key.StatusId,
      ExpenseTypeId: row.data.ExpenseTypeId ? row.data.ExpenseTypeId : row.key.ExpenseTypeId,
      Value: row.data.Value ? row.data.Value : row.key.Value,
      CarrierId: row.data.CarrierId ? row.data.CarrierId : row.key.CarrierId,
      EmployeeId: row.data.EmployeeId ? row.data.EmployeeId : row.key.EmployeeId,
      CategoryId: this.portalTypeService.getApprovalFunctionFromPortalType(this.portalType) ? 9 : 6,
      Date: row.data.Date ? moment(row.data.Date).format() : row.key.Date,
      Comment: row.data.Comment !== undefined ? row.data.Comment : row.key.Comment
    };
  }

  onProjectValueChanged(e: any, cell: any) {
    const selectedProject = e.component.option("selectedItem");
    if (selectedProject) {
      cell.setValue(selectedProject);
    }
  }

  setProjectValue(newData: any, value: any) {
    newData.CarrierId = value && value.CarrierId;
    newData.ProjectNumberAndName = value && value.ProjectDisplayName;
  }

  async onInitNewRow(e: any) {
    this.selectedTimeRecord ? this.initNewRowConnectedToTime(e) : this.initNewRowConnectedNotConnectedToTime(e);
    this.isClone = false;
  }

  private initNewRowConnectedToTime(e: any) {
    if (!this.isClone) {
      e.data.ExpenseId = -1;
      e.data.CarrierId = this.selectedTimeRecord.CarrierId;
    } else {
      e.data.ExpenseId = this.cloneData.ExpenseId;
      e.data.ExpenseTypeId = this.cloneData.ExpenseTypeId;
      e.data.Value = this.cloneData.Value;
      e.data.UnitDescription = this.cloneData.UnitDescription;
      e.data.Comment = this.cloneData.Comment;
    }
  }

  private initNewRowConnectedNotConnectedToTime(e: any) {
    if (!this.isClone) {
      e.data.ExpenseId = -1;
      e.data.StatusId = 3;
    } else {
      e.data.ExpenseId = this.cloneData.ExpenseId;
      e.data.StatusId = this.cloneData.StatusId;
      e.data.ExpenseTypeId = this.cloneData.ExpenseTypeId;
      e.data.CarrierId = this.cloneData.CarrierId;
      e.data.ProjectNumberAndName = this.cloneData.ProjectNumberAndName;
      e.data.EmployeeId = this.cloneData.EmployeeId;
      e.data.Date = this.cloneData.Date;
      e.data.Value = this.cloneData.Value;
      e.data.UnitDescription = this.cloneData.UnitDescription;
      e.data.Comment = this.cloneData.Comment;
    }
  }

  setAllowEdit(allow: boolean) {
    this.allowEdit = allow;
  }

  onLoading(loading: boolean) {
    this.loading.emit(loading);
  }

  private approve() {
    if (this.selectedTimeRecord) { return; }

    const selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();

    if (selectedRowsData.length === 0) { return; }

    this.onLoading(true);

    const expenseIds: number[] = [];

    for (let i = 0; i < selectedRowsData.length; i++) {
      if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
        selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
        (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
          continue;
      }
      expenseIds.push(selectedRowsData[i].ExpenseId);
    }

    if (expenseIds.length === 0) { return; }

    if (expenseIds.length > 100) {
      let startIndex = 0;
      let endIndex = 100;
      let arrayRequests = [];

      while (endIndex <= expenseIds.length) {
        let expenseIdsSliced = expenseIds.slice(startIndex, endIndex);
        if (expenseIdsSliced.length === 0) {
          break;
        }

        let requestConfirm = this.timeService.approveExpenseByExpenseIds(expenseIdsSliced, this.approvalFunction);
        arrayRequests.push(requestConfirm);
        startIndex = endIndex;
        endIndex = endIndex + 100 > expenseIds.length ? expenseIds.length : endIndex + 100;
        if (startIndex > expenseIds.length) {
          break;
        }
      }
      forkJoin(arrayRequests).subscribe((results: any) => {
        this.onLoading(false);
        let bAllOk = true;
        for(let result of results){
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
            bAllOk = false;
          }
        }
        if (bAllOk) {
          let okMessage = this.languageService.LNG("Label.Expenses") + " " + this.languageService.LNG("Label.Is.Approved");
          this.amsNotifierService.success(okMessage);
          this.expensesGrid.selectedRowKeys = [];
        }

        this.selectedTimeRecord ? this.loadExpenses() : this.loadExpensesNotConnectedToTime();
      });
    }
    else {
      this.timeService.approveExpenseByExpenseIds(expenseIds, this.approvalFunction).subscribe((result: any) => {

        this.onLoading(false);

        if (result.IsSuccess) {
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }
          this.expensesGrid.selectedRowKeys = [];
          let okMessage = this.languageService.LNG("Label.Expenses") + " " + this.languageService.LNG("Label.Is.Approved");
          this.amsNotifierService.success(okMessage);
          this.selectedTimeRecord ? this.loadExpenses() : this.loadExpensesNotConnectedToTime();
        }
        else {
          this.amsNotifierService.error(result.ErrorMessage);
        }
      }, error => {
        this.onLoading(false);
      });
    }
  }

  private reset(statusId: number) {
    if (this.selectedTimeRecord) { return; }

    const selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();

    if (selectedRowsData.length === 0) { return; }

    this.onLoading(true);

    const expenseIds: number[] = [];

    for (let i = 0; i < selectedRowsData.length; i++) {
      if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
        selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
        (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
          continue;
      }
      expenseIds.push(selectedRowsData[i].ExpenseId);
    }

    if (expenseIds.length === 0) { return; }

    if (expenseIds.length > 100) {
      let startIndex = 0;
      let endIndex = 100;
      let arrayRequests = [];

      while (endIndex <= expenseIds.length) {
        let expenseIdsSliced = expenseIds.slice(startIndex, endIndex);
        if (expenseIdsSliced.length === 0) {
          break;
        }

        let requestReset = this.timeService.resetExpenseByExpenseIds(expenseIdsSliced, statusId, this.approvalFunction);
        arrayRequests.push(requestReset);
        startIndex = endIndex;
        endIndex = endIndex + 100 > expenseIds.length ? expenseIds.length : endIndex + 100;
        if (startIndex > expenseIds.length) {
          break;
        }
      }
      forkJoin(arrayRequests).subscribe((results: any) => {

        this.onLoading(false);
        let bAllOk = true;
        for(let result of results){
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
            bAllOk = false;
          }
        }
        if (bAllOk) {
          this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
          this.expensesGrid.selectedRowKeys = [];
        }

        this.selectedTimeRecord ? this.loadExpenses() : this.loadExpensesNotConnectedToTime();
      });
    }
    else {
      this.timeService.resetExpenseByExpenseIds(expenseIds, statusId, this.approvalFunction).subscribe((result: any) => { // success path

        this.onLoading(false);

        if (result.IsSuccess) {
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }
          else {
            this.expensesGrid.selectedRowKeys = [];
            this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
          }
          this.selectedTimeRecord ? this.loadExpenses() : this.loadExpensesNotConnectedToTime();
        }
        else {
          this.amsNotifierService.error(result.ErrorMessage);
        }
      }, error => {
        this.onLoading(false);
      });
    }
  }

  //prevent all rows from being confirmed when none are selected if the page has multiple grids. Then some of the other grids may have selected rows
  onConfirm(preventConfirmAll = false): Promise<boolean> {

    this.loading.emit(true);
    return new Promise(resolve => {
      let expenseIds: number[] = [];
      let selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();
      if (selectedRowsData.length == 0 && !preventConfirmAll) {
        this.expenses.forEach(expense => {

          selectedRowsData.push(expense);
        });
      }

      if (selectedRowsData.length > 0) {
        for (let i = 0; i < selectedRowsData.length; i++) {
          //Eirik 11.06.21 v2.0.32.9 task 1200 - if user is supervisor and the time post has another SupervisorId, then make it unapprovable for user
          if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
            selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
            (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
              continue;
          }
          expenseIds.push(selectedRowsData[i].ExpenseId);
        }

        if (expenseIds.length > 0) {
          if (expenseIds.length > 100) {
            let startIndex = 0;
            let endIndex = 100;
            let arrayRequests = [];
            while (endIndex <= expenseIds.length) {
              let expenseIdsSliced = expenseIds.slice(startIndex, endIndex);
              if (expenseIdsSliced.length === 0) {
                break;
              }

              let requestConfirm = this.timeService.confirmExpenseByExpenseIds(expenseIdsSliced);
              arrayRequests.push(requestConfirm);
              startIndex = endIndex;
              endIndex = endIndex + 100 > expenseIds.length ? expenseIds.length : endIndex + 100;
              if (startIndex > expenseIds.length) {
                break;
              }
            }
            forkJoin(arrayRequests).subscribe((results: any) => {

              this.loading.emit(false);
              let bAllOk = true;
              for(let result of results){
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                  bAllOk = false;
                }
              }
              if (bAllOk) {
                let okMessage = this.languageService.LNG("Label.Expenses") + " " + this.languageService.LNG("Label.Is.Confirm");
                this.amsNotifierService.success(okMessage);
                this.expensesGrid.selectedRowKeys = [];
              }
              resolve(true);
            });
          }
          else {
            this.timeService.confirmExpenseByExpenseIds(expenseIds).subscribe((result: any) => { // success path

              this.loading.emit(false);

              if (result.IsSuccess) {
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                }
                this.expensesGrid.selectedRowKeys = [];
                let okMessage = this.languageService.LNG("Label.Expenses") + " " + this.languageService.LNG("Label.Is.Confirm");
                this.amsNotifierService.success(okMessage);
                resolve(true);
              }
              else {
                this.amsNotifierService.error(result.ErrorMessage);
                resolve(false);
              }
            }, error => { // error path;
              this.loading.emit(false);
              resolve(false);
            });
          }
        }
        else {
          this.amsNotifierService.info(this.languageService.LNG("Label.No.Expense.Posts.To.Confirm"));
          this.loading.emit(false);
          resolve(false);
        }
      }
      else {
        resolve(false);
      }
    })
  }

  //prevent all rows from being approved when none are selected if the page has multiple grids. Then some of the other grids may have selected rows
  onApprove(preventApproveAll = false): Promise<boolean> {

    this.loading.emit(true);
    return new Promise(resolve => {
      let expenseIds: number[] = [];
      let selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();
      if (selectedRowsData.length == 0 && !preventApproveAll) {
        this.expenses.forEach(expense => {
          selectedRowsData.push(expense);
        });
      }

      if (selectedRowsData.length > 0) {
        for (let i = 0; i < selectedRowsData.length; i++) {
          //Eirik 11.06.21 v2.0.32.9 task 1200 - if user is supervisor and the time post has another SupervisorId, then make it unapprovable for user
          if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
            selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
            (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
              continue;
          }
          expenseIds.push(selectedRowsData[i].ExpenseId);
        }

        if (expenseIds.length > 0) {
          if (expenseIds.length > 100) {
            let startIndex = 0;
            let endIndex = 100;
            let arrayRequests = [];
            while (endIndex <= expenseIds.length) {
              let expenseIdsSliced = expenseIds.slice(startIndex, endIndex);
              if (expenseIdsSliced.length === 0) {
                break;
              }

              let requestConfirm = this.timeService.approveExpenseByExpenseIds(expenseIdsSliced, this.approvalFunction);
              arrayRequests.push(requestConfirm);
              startIndex = endIndex;
              endIndex = endIndex + 100 > expenseIds.length ? expenseIds.length : endIndex + 100;
              if (startIndex > expenseIds.length) {
                break;
              }
            }
            forkJoin(arrayRequests).subscribe((results: any) => {

              this.loading.emit(false);
              let bAllOk = true;
              for(let result of results){
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                  bAllOk = false;
                }
              }
              if (bAllOk) {
                let okMessage = this.languageService.LNG("Label.Expenses") + " " + this.languageService.LNG("Label.Is.Approved");
                this.amsNotifierService.success(okMessage);
                this.expensesGrid.selectedRowKeys = [];
              }
              resolve(true);
            });
          }
          else {
            this.timeService.approveExpenseByExpenseIds(expenseIds, this.approvalFunction).subscribe((result: any) => { // success path

              this.loading.emit(false);

              if (result.IsSuccess) {
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                }
                this.expensesGrid.selectedRowKeys = [];
                let okMessage = this.languageService.LNG("Label.Expenses") + " " + this.languageService.LNG("Label.Is.Approved");
                this.amsNotifierService.success(okMessage);
                resolve(true);
              }
              else {
                this.amsNotifierService.error(result.ErrorMessage);
                resolve(false);
              }
            }, error => { // error path;
              this.loading.emit(false);
              resolve(false);
            });
          }
        }
        else {
          this.amsNotifierService.info(this.languageService.LNG("Label.No.Expense.Posts.To.Approve"));
          this.loading.emit(false);
          resolve(false);
        }
      }
      else {
        resolve(false);
      }
    })
  }

  onReset(statusId: number): Promise<boolean> {
    this.loading.emit(true);
    return new Promise(resolve => {
      let expenseIds: number[] = [];
      let selectedRowsData = this.expensesGrid.instance.getSelectedRowsData();

      if (selectedRowsData.length > 0) {
        for (let i = 0; i < selectedRowsData.length; i++) {
          if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
            selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
            (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
              continue;
          }
          expenseIds.push(selectedRowsData[i].ExpenseId);
        }

        if (expenseIds.length > 0) {
          if (expenseIds.length > 100) {
            let startIndex = 0;
            let endIndex = 100;
            let arrayRequests = [];
            while (endIndex <= expenseIds.length) {
              let expenseIdsSliced = expenseIds.slice(startIndex, endIndex);
              if (expenseIdsSliced.length === 0) {
                break;
              }

              let requestReset = this.timeService.resetExpenseByExpenseIds(expenseIdsSliced, statusId, this.approvalFunction);
              arrayRequests.push(requestReset);
              startIndex = endIndex;
              endIndex = endIndex + 100 > expenseIds.length ? expenseIds.length : endIndex + 100;
              if (startIndex > expenseIds.length) {
                break;
              }
            }
            forkJoin(arrayRequests).subscribe((results: any) => {

              this.loading.emit(false);
              let bAllOk = true;
              for(let result of results){
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                  bAllOk = false;
                }
              }
              if (bAllOk) {
                this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
                this.expensesGrid.selectedRowKeys = [];
              }
              resolve(true);
            });
          }
          else {
            this.timeService.resetExpenseByExpenseIds(expenseIds, statusId, this.approvalFunction).subscribe((result: any) => { // success path

              this.loading.emit(false);

              if (result.IsSuccess) {
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                }
                else {
                  this.expensesGrid.selectedRowKeys = [];
                  this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
                }
                resolve(true);
              }
              else {
                this.amsNotifierService.error(result.ErrorMessage);
                resolve(false);
              }
            }, error => {
              this.loading.emit(false);
              resolve(false);
            });
          }
        }
        else {
          this.amsNotifierService.info(this.languageService.LNG("Label.No.Posts.To.Reset"));
          this.loading.emit(false);
          resolve(false);
        }
      }
      else {
        resolve(false);
      }
    });
  }
}
